import React, { Fragment, useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import ReactModal from "react-modal";
import Select from "react-select";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import * as Sentry from "@sentry/react";
import { useDebouncedCallback } from "use-debounce";

import PageHeaderRowSearch from "../shared/page-header-row-search";
import { columns } from "./items-columns";
import axios from "axios";
import ai from "../../helpers/axios";
import Search from "../search";
import Loader from "../loader";
import { LoadingButton } from "../shared/loading-button";
import TextInput from "../text-input";
import TextAreaInput from "../textarea-input";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        isLoading: true,
      };
    }

    case "dataDownloaded": {
      return {
        ...state,
        isLoading: false,
        data: action?.payload?.data?.data || [],
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "rowSelection": {
      return {
        ...state,
        selectedRows: action.payload.selectedRows,
      };
    }

    case "closeModal": {
      return {
        ...state,
        selectedItem: null,
        isEditModalOpen: false,
        isDeleteModalOpen: false,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "selectedItemFieldChanged": {
      return {
        ...state,
        selectedItem: {
          ...(state.selectedItem || {}),
          [action.field]: action.value,
        },
      };
    }

    default:
      break;
  }
  return state;
};

const initialState = {
  isLoading: true,
  isSaving: false,
  data: [],
  categories: [],
  tags: [],
  totalLength: 0,
  selectedItem: null,
  isEditModalOpen: false,
  isDeleteModalOpen: false,
  query: "",
  categoryFilters: [],
  subCategoryFilters: [],
  tagFilters: [],
  countPerPage: 25,
  currentPage: 1,
  sortBy: "name",
  sortDirection: "asc",
  refreshDataToken: Date.now(),
};

const CateringItems = () => {
  const history = useHistory();
  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();
  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const userRoles = user && user["https://new.evojets.com/roles"];
  const isSuperAdmin =
    userRoles.some((role) => "Super Admin" === role);

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error("You do not have permission to view this page.");
      history.push("/");
    }
  }, [isSuperAdmin, history]);

  useEffect(() => {
    document.title = "Catering - Items | evoJets HQ";
  }, []);

  const getData = useCallback(async (params) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({
      type: "dataBeganDownloading",
    });

    try {
      const response = await ai
        .auth(token)
        .get(`/api/catering/items`, {
          params,
          signal,
        });

      if (response?.data) {
        dispatch({
          type: "dataDownloaded",
          payload: {
            data: response.data,
          },
        });
      }
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error?.response?.data);
      Sentry.captureException(error);
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getCateringCategories = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .get(`/api/catering/categories`)
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "categories",
        value: response.data.data,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getCateringTags = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .get(`/api/catering/tags`)
      .catch((error) => {
        console.error(error.response.data);
        Sentry.captureException(error);
      });

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "tags",
        value: response.data.data,
      });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const debouncedGetData = useDebouncedCallback(
    (params) => {
      getData(params);
    },
    500,
  );

  useEffect(() => {
    debouncedGetData({
      query: state.query,
      categoryFilters: state.categoryFilters,
      subCategoryFilters: state.subCategoryFilters,
      tagFilters: state.tagFilters,
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
    });
  }, [
    debouncedGetData,
    state.query,
    state.categoryFilters,
    state.subCategoryFilters,
    state.tagFilters,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
    state.refreshDataToken,
  ]);

  useEffect(() => {
    getCateringCategories();
    getCateringTags();
  }, [getCateringTags, getCateringCategories]);

  const handlePageChange = useCallback((page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  }, []);

  const handleRowsPerPageChange = useCallback((currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  }, []);

  const handleSort = useCallback((column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  }, [state.sortBy, state.sortDirection]);

  const handleSearchInputChange = useCallback((event) => {
    let query = event.target.value.toLowerCase();
    dispatch({ type: "search", query });
  }, []);

  const handleAdd = useCallback((event) => {
    event.preventDefault();

    dispatch({
      type: "fieldChanged",
      field: "isEditModalOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: {},
    });
  }, []);

  const handleEdit = useCallback((event, item) => {
    event.preventDefault();

    dispatch({
      type: "fieldChanged",
      field: "isEditModalOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (
      !state.selectedItem ||
      !state.selectedItem.name ||
      !state.selectedItem.description ||
      !state.selectedItem.categoryId
    ) {
      return toast.error(
        "Please fill in all required fields and retry.",
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);

      return loginWithRedirect();
    });

    const data = {
      name: state.selectedItem.name,
      description: state.selectedItem.description,
      categoryId: state.selectedItem.categoryId,
      subCategoryId: state.selectedItem.subCategoryId,
      tagIds: state.selectedItem.tagIds,
      servings: state.selectedItem.servings,
      order: parseInt(state.selectedItem.order) || null,
    };
    let response;
    if (state.selectedItem._id) {
      response = await ai
        .auth(token)
        .put(`/api/catering/items/${state.selectedItem._id}`, data)
        .catch((error) => {
          toast.error(
            getApiErrorMessage(error, "Couldn't save the catering item."),
          );
          Sentry.captureException(error);
        });
    } else {
      response = await ai
        .auth(token)
        .post(`/api/catering/items`, data)
        .catch((error) => {
          toast.error(
            getApiErrorMessage(error, "Couldn't save the catering item."),
          );
          Sentry.captureException(error);
        });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });

      dispatch({
        type: "closeModal",
      });
    }
  }, [state.selectedItem, getAccessTokenSilently, loginWithRedirect]);

  const handleDelete = useCallback((event, item) => {
    event.preventDefault();

    dispatch({
      type: "fieldChanged",
      field: "isDeleteModalOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "selectedItem",
      value: item,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!state.selectedItem) {
      return;
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const response = await ai
      .auth(token)
      .delete(`/api/catering/items/${state.selectedItem._id}`);

    if (response?.data) {
      dispatch({
        type: "fieldChanged",
        field: "refreshDataToken",
        value: Date.now(),
      });
    }

    dispatch({
      type: "fieldChanged",
      field: "isSaving",
      value: false,
    });

    dispatch({
      type: "closeModal",
    });
  }, [getAccessTokenSilently, loginWithRedirect, state.selectedItem]);

  const handleCloseModal = useCallback(() => {
    dispatch({
      type: "closeModal",
    });
  }, []);

  const columnsConfig = useMemo(() => ([
    ...columns,
    {
      name: "Actions",
      selector: "actions",
      sortable: false,
      width: "90px",
      cell: (row) => {
        return (
          <div className="d-flex justify-content-end">
            <button
              className="base-button icon-button icon-button-small evo-blue"
              title="Edit Featured One Way trip"
              onClick={(event) => handleEdit(event, row)}
            >
              <span className="material-icons">edit</span>
            </button>

            <button
              className="base-button icon-button icon-button-small btn-danger ml-2"
              title="Delete Featured One Way trip"
              onClick={(event) => handleDelete(event, row)}
            >
              <span className="material-icons">delete</span>
            </button>
          </div>
        );
      },
    },
  ]), [handleEdit, handleDelete]);

  const handleInputChange = useCallback((event) => {
    dispatch({
      type: "selectedItemFieldChanged",
      field: event.currentTarget.name,
      value: event.currentTarget.value,
    });
  }, []);

  const categoryOptions = useMemo(() => {
    if (!state.categories?.length) {
      return [];
    }

    return state.categories.map(({ _id, name }) => ({
      value: _id,
      label: name,
    }));
  }, [state.categories]);

  const subCategoryOptions = useMemo(() => {
    if (!state.categories?.length) {
      return [];
    }

    if (!state.selectedItem?.categoryId) {
      return [];
    }

    return (state.categories.find(({ _id }) => _id === state.selectedItem.categoryId)?.subCategories || []).map(({
      _id,
      name,
    }) => ({
      value: _id,
      label: name,
    }));
  }, [state.categories, state.selectedItem]);

  const subCategoryFilterOptions = useMemo(() => {
    if (!state.categories?.length) {
      return [];
    }

    if (!state.categoryFilters?.length) {
      return [];
    }

    return state.categories.filter(({ _id }) => state.categoryFilters.includes(_id)).map(({ subCategories }) => subCategories).flat().map(({
      _id,
      name,
    }) => ({
      value: _id,
      label: name,
    }));
  }, [state.categories, state.categoryFilters]);

  const tagsOptions = useMemo(() => {
    if (!state.tags?.length) {
      return [];
    }

    return state.tags.map(({ _id, name }) => ({
      value: _id,
      label: name,
    }));
  }, [state.tags]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={
          <h1 className="page-title d-flex">
            Catering - Items

            <button
              className="base-button icon-button icon-button-small evo-dark-green ml-2"
              title="Add Category"
              onClick={handleAdd}
              disabled={state.isSaving}
            >
              <span className="material-icons">add_circle</span>
            </button>
          </h1>
        }
        breadcrumbs={[{ link: "/catering-items", label: "Catering - Items" }]}
        extraClasses="page-header-catering"
        rightColumn={
          <div className="page-header-two-column-actions-row">
            <Search
              onSearch={handleSearchInputChange}
              placeholder="Search"
              query={state.query}
            />

            <Select
              className="catering-filter"
              isMulti
              classNamePrefix="select"
              options={categoryOptions}
              placeholder="Filter by Category"
              onChange={(selectedValue) => {
                dispatch({
                  type: "fieldChanged",
                  field: "categoryFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
                dispatch({
                  type: "fieldChanged",
                  field: "subCategoryFilters",
                  value: [],
                });
              }}
              value={categoryOptions?.filter(({ value }) => state.categoryFilters?.includes(value))}
              name="categoryFilters"
              required
            />

            <Select
              className="catering-filter"
              isMulti
              classNamePrefix="select"
              options={subCategoryFilterOptions}
              placeholder="Filter by Subcategory"
              onChange={(selectedValue) => {
                dispatch({
                  type: "fieldChanged",
                  field: "subCategoryFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              isDisabled={!state.categoryFilters?.length}
              value={subCategoryFilterOptions?.filter(({ value }) => state.subCategoryFilters?.includes(value))}
              name="subCategoryFilters"
              required
            />

            <Select
              className="catering-filter"
              isMulti
              classNamePrefix="select"
              options={tagsOptions}
              placeholder="Filter by Dietary Tags"
              onChange={(selectedValue) => {
                dispatch({
                  type: "fieldChanged",
                  field: "tagFilters",
                  value: selectedValue?.map(({ value }) => value) || [],
                });
              }}
              value={tagsOptions?.filter(({ value }) => state.tagFilters?.includes(value))}
              name="tagFilters"
              required
            />
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={columnsConfig}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[25, 50, 100, 150, 200, 250, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.isLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
        />
      </div>

      <ReactModal
        isOpen={state.isEditModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            border: "none",
            maxWidth: "700px",
            maxHeight: "80vh",
            minWidth: "calc(min(700px, 95vw))",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header">
          <h4>{state.selectedItem?._id ? "Edit" : "Add"} Catering Item</h4>
        </div>

        <div className="modal-body width-100 modal-body-with-scroll">
          <form className="w-100 base-form mt-10">
            <div className="w-100 d-flex gap-20">
              <TextInput
                containerClasses="flex-grow-1"
                name="name"
                label="Name"
                placeholder="Enter Item Name"
                required
                handleChange={handleInputChange}
                value={state.selectedItem?.name}
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <TextAreaInput
                containerClasses="flex-grow-1"
                label="Short Description"
                placeholder="Enter the item’s Short Description"
                type="text"
                name="description"
                required
                handleChange={handleInputChange}
                value={state.selectedItem?.description}
              />
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-half">
                <label htmlFor="categoryId" className="base-label">
                  Category <span className="required">*</span>
                </label>

                <Select
                  classNamePrefix="select"
                  options={categoryOptions}
                  placeholder="Select Category"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedItemFieldChanged",
                      field: "categoryId",
                      value: selectedValue?.value,
                    });
                    dispatch({
                      type: "selectedItemFieldChanged",
                      field: "subCategoryId",
                      value: null,
                    });
                  }}
                  value={categoryOptions?.find(({ value }) => value === state.selectedItem?.categoryId)}
                  name="categoryId"
                  required
                  menuPosition="fixed"
                />
              </div>

              <div className="form-group flex-half">
                <label htmlFor="subCategoryId" className="base-label">
                  Subcategory
                </label>

                <Select
                  key={state.selectedItem?.categoryId}
                  classNamePrefix="select"
                  options={subCategoryOptions}
                  placeholder="Select Subcategory"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedItemFieldChanged",
                      field: "subCategoryId",
                      value: selectedValue?.value,
                    });
                  }}
                  isDisabled={!state.selectedItem?.categoryId}
                  value={state.selectedItem?.categoryId ? subCategoryOptions?.find(({ value }) => value === state.selectedItem?.subCategoryId) : null}
                  name="subCategoryId"
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <div className="form-group flex-grow-1">
                <label htmlFor="tagIds" className="base-label">
                  Dietary Tags
                </label>

                <Select
                  isMulti
                  classNamePrefix="select"
                  options={tagsOptions}
                  placeholder="Select one or multiple Dietary Tags"
                  onChange={(selectedValue) => {
                    dispatch({
                      type: "selectedItemFieldChanged",
                      field: "tagIds",
                      value: selectedValue?.map(({ value }) => value) || [],
                    });
                  }}
                  value={tagsOptions?.filter(({ value }) => state.selectedItem?.tagIds?.includes(value))}
                  name="tagIds"
                  required
                  menuPosition="fixed"
                />
              </div>
            </div>

            <div className="w-100 d-flex gap-20">
              <TextInput
                containerClasses="flex-half"
                name="servings"
                label="Servings"
                placeholder="Enter Servings Type"
                handleChange={handleInputChange}
                value={state.selectedItem?.servings}
              />

              <TextInput
                name="order"
                label="Sort Order"
                placeholder="Enter Custom Sort Order"
                handleChange={handleInputChange}
                value={state.selectedItem?.order}
                type="number"
                step="1"
                min="1"
                className="mb-0 base-input"
                containerClasses="flex-half"
              />
            </div>
          </form>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            onClick={handleSubmit}
            isLoading={state.isSaving}
          >
            Submit
          </LoadingButton>

          <button
            className="base-button btn-danger flex-half"
            type="button"
            onClick={handleCloseModal}
            disabled={state.isSaving}
          >
            Cancel
          </button>
        </div>
      </ReactModal>

      <ReactModal
        isOpen={state.isDeleteModalOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Catering Item?</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            Are you sure you want to delete this catering item from the system? This action is permanent and cannot be
            undone.
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={handleCloseModal}
              disabled={state.isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteConfirm}
              isLoading={state.isSaving}
            >
              Delete Catering Item
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default CateringItems;
